import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, LogOut, Moon, ArrowLeft } from "lucide-react";
import { cn } from "../../../utils/function";
import { useTheme } from "../../../context/theme-context";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

// Deliberately one item — per explicit requirement, the reseller role only
// ever accesses Convo Marketing (pricing), not CRM/Automate/any other
// module. Mirrors global-admin-sidebar/sidebar.tsx's structure exactly so
// the two "own dashboard" areas feel consistent; add more here only if
// resellers are actually given more surface area later.
const NAV_ITEMS = [
  { id: "dashboard", label: "Convo Marketing", icon: LayoutDashboard, path: "/reseller/dashboard" },
];

const ResellerSidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark: darkMode, toggleTheme } = useTheme();

  const handleSignOut = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("resetOtpCode");
    localStorage.removeItem("role");
    navigate("/");
  };

  return (
    <div
      className={`flex flex-col h-full m-3 rounded-lg bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 transition-all duration-300 ${
        open ? "w-[257px]" : "w-[64px]"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        {open ? (
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold flex text-gray-800 dark:text-white tracking-tight">
              ConvoHub <span className="text-blue-500 font-semibold">Reseller</span>
            </span>
            <button
              className="ml-auto flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={onToggle}
              title="Collapse"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            className="mx-auto inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            onClick={onToggle}
            title="Expand"
          >
            <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <Link key={item.id} to={item.path} className="block">
              <div
                className={cn(
                  "flex items-center px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer select-none",
                  open ? "gap-3.5 justify-start" : "justify-center",
                  active
                    ? "text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800"
                    : "text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                <Icon
                  className={cn("w-5 h-5 shrink-0", active ? "text-gray-800 dark:text-gray-100" : "text-[#7B7B7B] font-semibold dark:text-gray-500")}
                />
                {open && <span className="tracking-tight">{item.label}</span>}
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="px-3 py-4 border-t border-gray-100 dark:border-gray-800 space-y-0.5">
        <div
          className={cn(
            "flex items-center px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
            open ? "justify-between" : "justify-center"
          )}
        >
          <div className={cn("flex items-center", open ? "gap-3.5" : "")}>
            <Moon className="w-5 h-5 text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" />
            {open && <span className="text-sm font-semibold text-gray-700 dark:text-white">Dark Mode</span>}
          </div>
          {open && (
            <button
              onClick={toggleTheme}
              className={cn(
                "relative w-9 h-5 rounded-full transition-colors",
                darkMode ? "bg-indigo-600" : "bg-gray-200 dark:bg-gray-700"
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform",
                  darkMode ? "translate-x-4" : "translate-x-0"
                )}
              />
            </button>
          )}
        </div>

        <button
          onClick={handleSignOut}
          className={cn(
            "w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
            open ? "gap-3.5 justify-start" : "justify-center"
          )}
        >
          <LogOut className="w-5 h-5 text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" />
          {open && <span className="tracking-tight">Log Out</span>}
        </button>
      </div>
    </div>
  );
};

export default ResellerSidebar;
